import * as React from 'react';
import {Helmet} from 'react-helmet';

import {Nut_Settings_User} from 'nutshell-core/settings-constants';

import {Orbs} from 'shells/ai-homepage/orbs/orbs';
import {AiHomepageDescription} from 'shells/ai-homepage/text/ai-homepage-description';
import {AiHomepageTitle} from 'shells/ai-homepage/text/ai-homepage-title';
import {KeyTriggeredAnimateIn} from 'shells/animation/key-triggered-animate-in';
import {headTitle} from 'shells/document/head-title';
import {Illustration} from 'shells/illustrations/illustration';
import {Stack} from 'shells/layout/stack/stack';
import {Link} from 'shells/link/link';
import {AiBadge} from 'shells/summary/ai-badge';

import {appHistory} from '../../../history';
import {writeLocalStorage} from '../../../util/localStorage';

import {useUpdateSetting} from '../../core/use-update-setting';
import {PageWrapper} from '../../layout/page-wrapper';

import styles from './welcome-page.module.css';

export function WelcomePage() {
    const [isTransitioningToHomepage, setIsTransitioningToHomepage] =
        React.useState<boolean>(false);

    const updateSetting = useUpdateSetting();

    React.useEffect(() => {
        updateSetting({
            settingName: Nut_Settings_User.SETTING_HAS_USER_VISITED_AI_HOMEPAGE,
            value: '1',
            isGlobal: false,
        });

        // Set local storage flag so we don't redirect to the welcome page
        // before client config is updated
        writeLocalStorage(Nut_Settings_User.SETTING_HAS_USER_VISITED_AI_HOMEPAGE, 'true');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleExploreClick = () => {
        setIsTransitioningToHomepage(true);
        setTimeout(() => {
            appHistory.push('/ai/home');
        }, 500);
    };

    const triggerKey = isTransitioningToHomepage ? 'transitioning' : 'not-transitioning';

    return (
        <PageWrapper>
            <Helmet>
                <title>{headTitle('AI')}</title>
            </Helmet>
            <div className={styles.container}>
                {/* @ts-expect-error */}
                <KeyTriggeredAnimateIn triggerKey={triggerKey}>
                    <Orbs
                        config={[
                            {top: 20, left: -50},
                            {top: -50, left: 70},
                            {top: 60, left: 30},
                            {top: 0, left: 200},
                            {top: 100, left: 130},
                        ]}
                    >
                        <div className={styles['content-container']}>
                            <Stack spacing={36}>
                                <AiBadge size='large' />
                                <Stack spacing={24}>
                                    <AiHomepageTitle text='Welcome to Nutshell AI' />
                                    <Stack spacing={20}>
                                        <AiHomepageDescription text='We believe the best technology feels invisible — quietly amplifying your work, not complicating it.' />
                                        <AiHomepageDescription text="Our team works hard each day to bring useful tools to businesses of all sizes. And in 2025, we've launched a number of time-saving features, powered by frontier AI models." />
                                        <AiHomepageDescription text="Nutshell AI works alongside you, taking care of what's routine so you can focus on what's real: the people, the deals, and the stories behind every connection." />
                                        <AiHomepageDescription text="Here you can discover all of Nutshell's AI capabilities, and some info on how they're saving time for your team. We've got more on the way this winter — stay tuned!" />
                                    </Stack>
                                </Stack>
                                <Link
                                    size='xlarge'
                                    variant='button-ai-purple-dark'
                                    widthFitContent={true}
                                    onClick={handleExploreClick}
                                >
                                    Explore
                                </Link>
                            </Stack>
                        </div>
                    </Orbs>
                </KeyTriggeredAnimateIn>
                <div className={styles['carousel-wrapper']}>
                    {/* @ts-expect-error */}
                    <KeyTriggeredAnimateIn triggerKey={triggerKey}>
                        <Illustration src='ai-homepage/ai-welcome-carousel' height='100%' />
                    </KeyTriggeredAnimateIn>
                </div>
            </div>
        </PageWrapper>
    );
}
